import { useMemo } from 'react'
import {
  Box,
  VStack,
  HStack,
  Text,
  Badge,
  Button,
  IconButton,
  useDisclosure,
} from '@chakra-ui/react';
import { toaster } from '@/components/ui/toaster'
import { Tooltip } from '@/components/ui/tooltip';
import { FiBriefcase, FiPlus, FiEdit2, FiTrash2, FiMapPin, FiHome } from 'react-icons/fi'
import { useNavigate } from 'react-router-dom'
import { useDeleteEmployment, useBestLocation } from '../../hooks/useEmployment'
import { EmploymentFormModal } from '../business/EmploymentFormModal'
import { parseScheduleJson, isCurrentlyAtWork, formatScheduleSummary } from '../../lib/workSchedule'
import type { ContactEmployment } from '../../types/contactDetail'
import { UnionOnly } from '../scoping/UnionOnly'

interface EmploymentSectionProps {
  contactId: string
  employments: ContactEmployment[]
  canEdit: boolean
}

export function EmploymentSection(props: EmploymentSectionProps) {
  return (
    <UnionOnly>
      <EmploymentSectionInner {...props} />
    </UnionOnly>
  )
}

function EmploymentSectionInner({ contactId, employments, canEdit }: EmploymentSectionProps) {
  const navigate = useNavigate()
  const addModal = useDisclosure()
  const deleteMutation = useDeleteEmployment(contactId)
  const { data: bestLocation } = useBestLocation(contactId)

  const handleDelete = async (employmentId: string, e: React.MouseEvent) => {
    e.stopPropagation()
    try {
      await deleteMutation.mutateAsync(employmentId)
      toaster.create({ title: 'Employment removed', type: 'success', duration: 2000 })
    } catch {
      toaster.create({ title: 'Failed to remove employment', type: 'error', duration: 3000 })
    }
  }

  return (
    <Box bg="surface" p={6} borderRadius="lg" shadow="md">
      <HStack justify="space-between" mb={4}>
        <HStack gap={3}>
          <Text fontSize="lg" fontWeight="semibold">
            Employment ({employments.length})
          </Text>
          {bestLocation && bestLocation.locationType !== 'unknown' && (
            <SmartFindBadge locationType={bestLocation.locationType} worksiteName={bestLocation.worksiteName} />
          )}
        </HStack>
        {canEdit && (
          <Button size="xs" colorPalette="brand" variant="outline" onClick={addModal.onOpen}><FiPlus />Add
                      </Button>
        )}
      </HStack>

      {employments.length === 0 ? (
        <Text color="gray.500" fontSize="sm">No employment records linked to this contact.</Text>
      ) : (
        <VStack gap={3} align="stretch">
          {employments.map(emp => (
            <EmploymentCard
              key={emp.employmentId}
              emp={emp}
              canEdit={canEdit}
              contactId={contactId}
              onDelete={handleDelete}
              onNavigate={() => navigate(`/dashboard/worksites/${emp.worksiteId}`)}
            />
          ))}
        </VStack>
      )}

      {/* Add employment modal */}
      {addModal.open && (
        <EmploymentFormModal
          isOpen={addModal.open}
          onClose={addModal.onClose}
          contactId={contactId}
        />
      )}
    </Box>
  );
}

function SmartFindBadge({ locationType, worksiteName }: { locationType: string; worksiteName?: string }) {
  if (locationType === 'work') {
    return (
      <Tooltip contentProps={{ fontSize: "xs" }} content={`Likely at work${worksiteName ? ` (${worksiteName})` : ''}`}>
        <Badge colorPalette="orange" fontSize="xs" display="flex" alignItems="center" gap={1}>
          <FiMapPin size={10} /> At work
        </Badge>
      </Tooltip>
    );
  }
  return (
    <Tooltip contentProps={{ fontSize: "xs" }} content="Likely at home">
      <Badge colorPalette="teal" fontSize="xs" display="flex" alignItems="center" gap={1}>
        <FiHome size={10} /> At home
      </Badge>
    </Tooltip>
  );
}

interface EmploymentCardProps {
  emp: ContactEmployment
  canEdit: boolean
  contactId: string
  onDelete: (id: string, e: React.MouseEvent) => void
  onNavigate: () => void
}

function EmploymentCard({ emp, canEdit, contactId, onDelete, onNavigate }: EmploymentCardProps) {
  const editModal = useDisclosure()
  const atWork = useMemo(() => isCurrentlyAtWork(emp.workScheduleJson), [emp.workScheduleJson])
  const scheduleSummary = useMemo(() => formatScheduleSummary(emp.workScheduleJson), [emp.workScheduleJson])

  return (
    <>
      <Box
        p={3}
        borderRadius="md"
        border="1px"
        borderColor={emp.isActive ? 'gray.200' : 'gray.100'}
        bg={emp.isActive ? 'white' : 'gray.50'}
        opacity={emp.isActive ? 1 : 0.7}
      >
        <HStack justify="space-between" align="flex-start">
          <HStack gap={3} align="flex-start" flex={1}>
            <Box boxSize={4} color="gray.400" mt={1} asChild><FiBriefcase /></Box>
            <Box flex={1}>
              <HStack gap={2} mb={1}>
                <Text
                  fontWeight="medium"
                  fontSize="sm"
                  cursor="pointer"
                  color="brand.600"
                  _hover={{ textDecoration: 'underline' }}
                  onClick={onNavigate}
                >
                  {emp.worksiteName}
                </Text>
                {emp.worksiteType && (
                  <Badge bg="info.subtle" color="info.fg" fontSize="xs">{emp.worksiteType}</Badge>
                )}
                {emp.isPrimary && (
                  <Badge colorPalette="green" fontSize="xs">Primary</Badge>
                )}
                {!emp.isActive && (
                  <Badge colorPalette="gray" fontSize="xs">Inactive</Badge>
                )}
                {emp.isActive && atWork && (
                  <Tooltip contentProps={{ fontSize: "xs", whiteSpace: "pre-line" }} content={scheduleSummary}>
                    <Badge colorPalette="orange" fontSize="xs">Now working</Badge>
                  </Tooltip>
                )}
                {emp.isActive && emp.workScheduleJson && !atWork && (
                  <Tooltip contentProps={{ fontSize: "xs", whiteSpace: "pre-line" }} content={scheduleSummary}>
                    <Badge colorPalette="gray" variant="outline" fontSize="xs">Off shift</Badge>
                  </Tooltip>
                )}
              </HStack>
              {emp.position && (
                <Text fontSize="sm" color="gray.600">{emp.position}</Text>
              )}
              {emp.department && (
                <Text fontSize="xs" color="gray.500">Dept: {emp.department}</Text>
              )}
              {emp.unionName && (
                <HStack gap={1} mt={1}>
                  <Badge colorPalette="purple" fontSize="xs">{emp.unionName}</Badge>
                  {emp.unionMemberNumber && (
                    <Text fontSize="xs" color="gray.500">#{emp.unionMemberNumber}</Text>
                  )}
                </HStack>
              )}
              {(emp.startDate || emp.endDate) && (
                <Text fontSize="xs" color="gray.400" mt={1}>
                  {emp.startDate ? new Date(emp.startDate).toLocaleDateString() : '?'}
                  {' — '}
                  {emp.endDate ? new Date(emp.endDate).toLocaleDateString() : 'Present'}
                </Text>
              )}
              {emp.workScheduleJson && (
                <Box mt={2} p={2} bg="gray.50" borderRadius="sm">
                  <Text fontSize="xs" fontWeight="medium" color="gray.500" mb={1}>Schedule</Text>
                  <HStack gap={4} flexWrap="wrap">
                    {(() => {
                      const schedule = parseScheduleJson(emp.workScheduleJson)
                      const dayKeys = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']
                      const shortNames: Record<string, string> = {
                        monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed', thursday: 'Thu',
                        friday: 'Fri', saturday: 'Sat', sunday: 'Sun',
                      }
                      return dayKeys
                        .filter(d => schedule[d] != null)
                        .map(d => (
                          <Text key={d} fontSize="xs" color="gray.600">
                            <Text as="span" fontWeight="medium">{shortNames[d]}:</Text>{' '}
                            {schedule[d]!.start}–{schedule[d]!.end}
                          </Text>
                        ))
                    })()}
                  </HStack>
                </Box>
              )}
            </Box>
          </HStack>
          {canEdit && (
            <HStack gap={1}>
              <IconButton
                aria-label="Edit"
                size="xs"
                variant="ghost"
                onClick={(e) => { e.stopPropagation(); editModal.onOpen() }}><FiEdit2 /></IconButton>
              <IconButton
                aria-label="Remove"
                size="xs"
                variant="ghost"
                colorPalette="red"
                onClick={(e) => onDelete(emp.employmentId, e)}><FiTrash2 /></IconButton>
            </HStack>
          )}
        </HStack>
      </Box>

      {/* Edit employment modal */}
      {editModal.open && (
        <EmploymentFormModal
          isOpen={editModal.open}
          onClose={editModal.onClose}
          contactId={contactId}
          employment={emp}
        />
      )}
    </>
  );
}
